import type { MapCloudPoint, MapPointCloudSetOptions } from '@gpx-spa/geo-domain'

const EARTH_RADIUS_M = 6_371_000

function toRad(deg: number): number {
  return (deg * Math.PI) / 180
}

function haversineMeters(
  a: { lat: number; lng: number },
  b: { lat: number; lng: number },
): number {
  const dLat = toRad(b.lat - a.lat)
  const dLng = toRad(b.lng - a.lng)
  const lat1 = toRad(a.lat)
  const lat2 = toRad(b.lat)
  const h =
    Math.sin(dLat / 2) ** 2 +
    Math.cos(lat1) * Math.cos(lat2) * Math.sin(dLng / 2) ** 2
  return 2 * EARTH_RADIUS_M * Math.asin(Math.min(1, Math.sqrt(h)))
}

/** Default zoom → min gap (meters) for `thinning.mode: 'auto'`. */
export function minGapMetersForZoom(zoom: number): number {
  if (zoom >= 15) return 0
  if (zoom >= 13) return 15
  if (zoom >= 11) return 40
  if (zoom >= 9) return 100
  return 250
}

function segmentIdOf(point: MapCloudPoint): string {
  const id = point.payload?.segmentId
  return typeof id === 'string' ? id : ''
}

/**
 * Keep first/last of each run; drop points closer than minGapMeters to last kept
 * (along array order = along path). Runs split on `payload.segmentId`.
 */
export function thinCloudPointsByMinGap(
  points: readonly MapCloudPoint[],
  minGapMeters: number,
): MapCloudPoint[] {
  if (points.length === 0) return []
  if (!(minGapMeters > 0)) return [...points]

  const out: MapCloudPoint[] = []
  let runStart = 0

  const flushRun = (endExclusive: number) => {
    if (endExclusive <= runStart) return
    const run = points.slice(runStart, endExclusive)
    if (run.length === 0) return
    if (run.length === 1) {
      out.push(run[0]!)
      return
    }
    out.push(run[0]!)
    let lastKept = run[0]!
    for (let i = 1; i < run.length - 1; i++) {
      const p = run[i]!
      if (haversineMeters(lastKept.position, p.position) >= minGapMeters) {
        out.push(p)
        lastKept = p
      }
    }
    const last = run[run.length - 1]!
    if (out[out.length - 1] !== last) {
      out.push(last)
    }
  }

  let prevId = segmentIdOf(points[0]!)
  for (let i = 1; i < points.length; i++) {
    const id = segmentIdOf(points[i]!)
    if (id !== prevId) {
      flushRun(i)
      runStart = i
      prevId = id
    }
  }
  flushRun(points.length)
  return out
}

export function resolvePointCloudThinning(
  points: readonly MapCloudPoint[],
  zoom: number,
  options: MapPointCloudSetOptions | undefined,
): MapCloudPoint[] {
  const thinning = options?.thinning
  if (thinning === false) return [...points]
  if (!thinning || thinning.strategy !== 'minGap') return [...points]
  if (thinning.mode === false) return [...points]
  const gap = minGapMetersForZoom(zoom)
  return thinCloudPointsByMinGap(points, gap)
}
